import logger from '../utils/logger.js';
import { apiService } from './apiService.js';

/**
 * Web Push notification service.
 * Handles service worker registration, push subscription management,
 * and communication with backend edge functions.
 */

/**
 * Check if the current browser supports Web Push.
 */
export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Check if the browser is Safari on iOS (iPad or iPhone).
 */
export function isIOSSafari() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

/**
 * Check if the app is running in standalone mode (added to Home Screen).
 */
export function isStandaloneMode() {
  if (typeof window === 'undefined') return false;
  return (
    window.navigator.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches
  );
}

/**
 * Get the current push permission state.
 * @returns {'granted' | 'denied' | 'default' | 'unsupported'}
 */
export function getPushPermissionState() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

/**
 * Register the service worker for push notifications.
 * @returns {Promise<ServiceWorkerRegistration | null>}
 */
export async function registerServiceWorker() {
  if (!isPushSupported()) return null;

  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    // Wait for the SW to become active — pushManager.subscribe() requires it
    if (!registration.active) {
      await new Promise((resolve) => {
        const sw = registration.installing || registration.waiting;
        if (!sw) { resolve(); return; }
        sw.addEventListener('statechange', function listener() {
          if (sw.state === 'activated') {
            sw.removeEventListener('statechange', listener);
            resolve();
          }
        });
      });
    }
    logger.log('[PushService] Service worker registered');
    return registration;
  } catch (err) {
    logger.error('[PushService] Service worker registration failed:', err);
    return null;
  }
}

/**
 * Convert a base64 VAPID key to a Uint8Array for PushManager.subscribe().
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Subscribe the current browser to push notifications and register with backend.
 * Triggers the browser permission prompt if needed.
 * @returns {Promise<{success: boolean, permissionState: string}>}
 */
export async function subscribeToPush() {
  if (!isPushSupported()) {
    return { success: false, permissionState: 'unsupported' };
  }

  try {
    const registration = await registerServiceWorker();
    if (!registration) {
      return { success: false, permissionState: getPushPermissionState() };
    }

    const vapidPublicKey = process.env.EXPO_PUBLIC_VAPID_PUBLIC_KEY;
    if (!vapidPublicKey) {
      logger.error('[PushService] VAPID public key not configured');
      return { success: false, permissionState: getPushPermissionState() };
    }

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });

    const permissionState = getPushPermissionState();
    if (permissionState !== 'granted') {
      return { success: false, permissionState };
    }

    // Extract subscription data
    const subscriptionJSON = subscription.toJSON();
    const { endpoint, keys } = subscriptionJSON;

    // Register with backend
    await apiService.invokeEdgeFunction('register-push-subscription', {
      body: {
        endpoint,
        keys: {
          p256dh: keys.p256dh,
          auth: keys.auth,
        },
      },
    });

    logger.log('[PushService] Push subscription registered');
    return { success: true, permissionState: 'granted' };
  } catch (err) {
    logger.error('[PushService] Subscribe failed:', err);
    return { success: false, permissionState: getPushPermissionState() };
  }
}

/**
 * Check if this device/browser has an active push subscription.
 * @returns {Promise<boolean>}
 */
export async function hasDevicePushSubscription() {
  if (!isPushSupported()) return false;
  try {
    // Use getRegistration() instead of .ready — .ready hangs forever
    // if no service worker is registered (e.g. in dev).
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration?.active) return false;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch {
    return false;
  }
}

/**
 * Unsubscribe the current browser from push notifications.
 * @returns {Promise<boolean>}
 */
export async function unsubscribeFromPush() {
  if (!isPushSupported()) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (!subscription) return true; // Already unsubscribed

    const { endpoint } = subscription.toJSON();

    // Unsubscribe from browser
    await subscription.unsubscribe();

    // Notify backend
    await apiService.invokeEdgeFunction('unregister-push-subscription', {
      body: { endpoint },
    });

    logger.log('[PushService] Push subscription removed');
    return true;
  } catch (err) {
    logger.error('[PushService] Unsubscribe failed:', err);
    return false;
  }
}
